/**
 * LE MATÉRIEL DE DESSIN : filtres, dorures et hachures, montés une fois pour toute la page.
 *
 * Tout ce qui suit est dessiné pour ce jeu-ci (design §1.3) : pas une icône de bibliothèque, pas
 * une texture achetée. Les hachures de biome, surtout, sont ce qui fera qu'une carte de Destinia
 * ressemble à une carte de Destinia et à rien d'autre.
 *
 * LES COULEURS PASSENT PAR DES CLASSES, jamais par un attribut `stroke="var(--herbe)"` : `var()`
 * n'est pas substitué dans un attribut de présentation SVG (il ne l'est que dans une déclaration
 * CSS). Les classes sont dans `styles/dessin.css`, et c'est aussi ce qui fait suivre le thème.
 */

/** Les hachures, par biome. Elles disent le terrain SANS QUE LA COULEUR PORTE SEULE (accessibilité
 * §5) : on lit la trame avant la teinte — d'où des motifs franchement différents les uns des
 * autres, et non six variations de pointillé. */
function Hachures() {
  return (
    <>
      {/* Forêt — des houppiers, en trois coups de plume. */}
      <pattern id="biome-foret" width="18" height="18" patternUnits="userSpaceOnUse">
        <path
          className="trame trame--foret"
          d="M3 14c1.6-2.4 2.6-4.4 3-6 .4 1.6 1.4 3.6 3 6M11 8c1.4-2 2.3-3.7 2.6-5 .4 1.3 1.2 3 2.6 5"
        />
      </pattern>

      {/* Montagne — des chevrons, l'un derrière l'autre. */}
      <pattern id="biome-montagne" width="20" height="16" patternUnits="userSpaceOnUse">
        <path className="trame trame--montagne" d="M1 13l5-8 5 8M9 13l5-7 5 7" />
      </pattern>

      {/* Marais — des tirets d'eau et un jonc. */}
      <pattern id="biome-marais" width="18" height="14" patternUnits="userSpaceOnUse">
        <path className="trame trame--marais" d="M1 5h6M10 5h6M4 11h6M13 12v-4" />
      </pattern>

      {/* Terres sèches — le pointillé de sable, et une dune. */}
      <pattern id="biome-desert" width="16" height="16" patternUnits="userSpaceOnUse">
        <circle className="trame-pleine trame--desert" cx="4" cy="4" r="0.9" />
        <circle className="trame-pleine trame--desert" cx="11" cy="9" r="0.9" />
        <path className="trame trame--desert" d="M2 13c2-2 5-2 7 0" />
      </pattern>

      {/* Ruines — des pierres tombées, jamais alignées. */}
      <pattern id="biome-ruines" width="18" height="18" patternUnits="userSpaceOnUse">
        <path className="trame trame--ruines" d="M2 3h6v4H2zM10 9h5v5h-5zM3 16h4" />
      </pattern>

      {/* Souterrain — la trame croisée : ce qui est sous la terre est plein. */}
      <pattern id="biome-souterrain" width="10" height="10" patternUnits="userSpaceOnUse">
        <path className="trame trame--souterrain" d="M0 10L10 0M-2 2L2 -2M8 12l4-4" />
      </pattern>

      {/* Ville — un plan de rues. */}
      <pattern id="biome-urbain" width="16" height="16" patternUnits="userSpaceOnUse">
        <path className="trame trame--urbain" d="M0 6h16M6 0v16M9 9h4v4H9z" />
      </pattern>

      {/* Côte — la houle, régulière. */}
      <pattern id="biome-cotier" width="20" height="12" patternUnits="userSpaceOnUse">
        <path className="trame trame--cotier" d="M0 4c3-3 7-3 10 0s7 3 10 0M0 10c3-3 7-3 10 0s7 3 10 0" />
      </pattern>
    </>
  );
}

export function Defs() {
  return (
    <svg aria-hidden="true" focusable="false" width="0" height="0" className="defs">
      <defs>
        {/* LE BORD FROISSÉ. Trois pour cent d'irrégularité (design §2) : `scale` à 4, pas à 40.
            Appliqué au FOND d'un panneau, jamais à son texte — un texte déplacé par un bruit est un
            texte qu'on ne lit plus. */}
        <filter id="bord-papier" x="-4%" y="-4%" width="108%" height="108%">
          <feTurbulence type="fractalNoise" baseFrequency="0.014" numOctaves="3" seed="7" result="bruit" />
          <feDisplacementMap in="SourceGraphic" in2="bruit" scale="4" xChannelSelector="R" yChannelSelector="G" />
        </filter>

        {/* La même chose, en plus franc : les bords déchirés (la mort, les sceaux). */}
        <filter id="bord-dechire" x="-8%" y="-8%" width="116%" height="116%">
          <feTurbulence type="fractalNoise" baseFrequency="0.03" numOctaves="4" seed="19" result="bruit" />
          <feDisplacementMap in="SourceGraphic" in2="bruit" scale="9" xChannelSelector="R" yChannelSelector="G" />
        </filter>

        {/* LA GRAVURE. Une lumière en haut, une ombre en bas — l'inverse du relief des interfaces :
            le mot est creusé DANS la page, il ne flotte pas dessus. */}
        <filter id="gravure" x="-15%" y="-40%" width="130%" height="200%">
          <feOffset in="SourceAlpha" dx="0" dy="1.6" result="bas" />
          <feGaussianBlur in="bas" stdDeviation="1" result="basFlou" />
          <feComposite in="basFlou" in2="SourceAlpha" operator="out" result="ombreBasse" />
          <feFlood className="flood-sombre" result="teinteBasse" />
          <feComposite in="teinteBasse" in2="ombreBasse" operator="in" result="creuxBas" />

          <feOffset in="SourceAlpha" dx="0" dy="-1.3" result="haut" />
          <feGaussianBlur in="haut" stdDeviation="0.8" result="hautFlou" />
          <feComposite in="hautFlou" in2="SourceAlpha" operator="out" result="lumiereHaute" />
          <feFlood className="flood-clair" result="teinteHaute" />
          <feComposite in="teinteHaute" in2="lumiereHaute" operator="in" result="creuxHaut" />

          <feMerge>
            <feMergeNode in="creuxBas" />
            <feMergeNode in="creuxHaut" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>

        {/* L'or filé : trois arrêts, parce que la lumière y passe UNE fois et repart. */}
        <linearGradient id="or-file" x1="0" y1="0" x2="0.3" y2="1">
          <stop className="arret-or-clair" offset="0%" />
          <stop className="arret-or" offset="44%" />
          <stop className="arret-or-clair" offset="62%" />
          <stop className="arret-or-sombre" offset="100%" />
        </linearGradient>

        <radialGradient id="cire" cx="0.35" cy="0.3" r="0.85">
          <stop className="arret-sang-pale" offset="0%" />
          <stop className="arret-sang" offset="60%" />
          <stop className="arret-sang-sombre" offset="100%" />
        </radialGradient>

        <Hachures />
      </defs>
    </svg>
  );
}

/** Le motif à employer pour un biome — et un repli lisible quand une aventure en invente un. */
const BIOMES_DESSINES = ["foret", "montagne", "marais", "desert", "ruines", "souterrain", "urbain", "cotier"];

export function hachureDe(biome: string): string {
  return BIOMES_DESSINES.includes(biome) ? `url(#biome-${biome})` : "url(#biome-ruines)";
}
